import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

const MIN_DURATION_MS = 500; // ignore accidental clicks
const MAX_DURATION_MS = 120000; // auto-stop at 2 minutes
const TIMER_TICK_MS = 250;

// Click-to-talk voice input for the chat. Consumed by ProjectChat.vue, which
// provides `input`, `send()`, `notice`, `inputDisabled`, `uploadingAttachments`,
// `awaitingReply` and `chatMode`. Flow: mic click starts a MediaRecorder
// session, a second click stops it, the audio is transcribed server-side
// (transcribeAudio callable) and the text is auto-sent through the normal
// send flow — identical for personal and communal mode.
export default {
  data() {
    return {
      voiceState: "idle", // "idle" | "recording" | "transcribing"
      voiceElapsedMs: 0,
      voiceRecorder: null,
      voiceStream: null,
      voiceChunks: [],
      voiceMime: "",
      voiceStartedAt: 0,
      voiceTimer: null,
      voiceCancelled: false,
      voiceAutoStopped: false,
      // Kept after a failed transcription so a mic click retries the same
      // recording instead of losing the user's speech.
      voiceLastBlob: null,
    };
  },
  computed: {
    voiceSupported() {
      return !!(
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder
      );
    },
    voiceElapsedLabel() {
      const s = Math.floor(this.voiceElapsedMs / 1000);
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    },
    micDisabled() {
      return (
        this.voiceState === "transcribing" ||
        this.inputDisabled ||
        this.uploadingAttachments ||
        (this.chatMode === "personal" && this.awaitingReply)
      );
    },
  },
  beforeUnmount() {
    this.teardownVoice();
  },
  methods: {
    pickVoiceMime() {
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
    },
    async toggleMic() {
      if (this.voiceState === "recording")
        return this.stopVoiceRecording(false);
      if (this.voiceState !== "idle" || this.micDisabled) return;
      if (this.voiceLastBlob) {
        const blob = this.voiceLastBlob;
        this.voiceLastBlob = null;
        return this.transcribeVoiceBlob(blob);
      }
      await this.startVoiceRecording();
    },
    async startVoiceRecording() {
      this.notice = "";
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.error("getUserMedia failed:", err);
        this.notice =
          err &&
          (err.name === "NotAllowedError" || err.name === "SecurityError")
            ? "Microphone access is blocked. Allow the microphone for this site in your browser settings and try again."
            : err && err.name === "NotFoundError"
            ? "No microphone found on this device."
            : "Could not start the microphone. Please try again.";
        return;
      }
      const mimeType = this.pickVoiceMime();
      let recorder;
      try {
        recorder = new MediaRecorder(
          stream,
          mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined
        );
      } catch (err) {
        stream.getTracks().forEach((t) => t.stop());
        console.error("MediaRecorder init failed:", err);
        this.notice = "Voice recording isn't supported in this browser.";
        return;
      }
      this.voiceStream = stream;
      this.voiceRecorder = recorder;
      this.voiceMime = recorder.mimeType || mimeType || "audio/webm";
      this.voiceChunks = [];
      this.voiceCancelled = false;
      this.voiceAutoStopped = false;
      this.voiceStartedAt = Date.now();
      this.voiceElapsedMs = 0;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) this.voiceChunks.push(e.data);
      };
      recorder.onstop = () => this.onVoiceRecorderStop();
      recorder.start();
      this.voiceState = "recording";
      window.addEventListener("keydown", this.onVoiceKeydown);
      this.voiceTimer = setInterval(() => {
        this.voiceElapsedMs = Date.now() - this.voiceStartedAt;
        if (this.voiceElapsedMs >= MAX_DURATION_MS) {
          this.voiceAutoStopped = true;
          this.stopVoiceRecording(false);
        }
      }, TIMER_TICK_MS);
    },
    stopVoiceRecording(cancel) {
      if (this.voiceState !== "recording" || !this.voiceRecorder) return;
      this.voiceCancelled = !!cancel;
      try {
        this.voiceRecorder.stop();
      } catch (e) {
        this.onVoiceRecorderStop();
      }
    },
    onVoiceRecorderStop() {
      const durationMs = Date.now() - this.voiceStartedAt;
      const chunks = this.voiceChunks;
      const mime = this.voiceMime;
      const cancelled = this.voiceCancelled;
      this.releaseVoiceHardware();
      if (cancelled || durationMs < MIN_DURATION_MS || !chunks.length) {
        this.voiceState = "idle";
        return;
      }
      if (this.voiceAutoStopped) {
        this.notice =
          "Recording stopped at the 2-minute limit — sending what was captured.";
      }
      this.transcribeVoiceBlob(new Blob(chunks, { type: mime }));
    },
    releaseVoiceHardware() {
      if (this.voiceTimer) {
        clearInterval(this.voiceTimer);
        this.voiceTimer = null;
      }
      window.removeEventListener("keydown", this.onVoiceKeydown);
      if (this.voiceStream) {
        this.voiceStream.getTracks().forEach((t) => t.stop());
        this.voiceStream = null;
      }
      this.voiceRecorder = null;
      this.voiceChunks = [];
    },
    onVoiceKeydown(e) {
      if (e.key === "Escape" && this.voiceState === "recording") {
        e.preventDefault();
        this.stopVoiceRecording(true);
      }
    },
    voiceBlobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () =>
          resolve(String(reader.result).split(",")[1] || "");
        reader.readAsDataURL(blob);
      });
    },
    async transcribeVoiceBlob(blob) {
      this.voiceState = "transcribing";
      try {
        const audio = await this.voiceBlobToBase64(blob);
        const transcribeAudio = httpsCallable(functions, "transcribeAudio", {
          timeout: 120000,
        });
        const res = await transcribeAudio({
          audio,
          mimeType: blob.type ? blob.type.split(";")[0] : "audio/webm",
        });
        const text = (res.data && res.data.text) || "";
        this.voiceState = "idle";
        await this.applyVoiceTranscript(text);
      } catch (error) {
        console.error("transcribeAudio failed:", error);
        this.voiceState = "idle";
        this.voiceLastBlob = blob;
        const detail =
          error && error.message && !/internal/i.test(error.message)
            ? error.message + " "
            : "Voice transcription failed. ";
        this.notice =
          detail + "Your recording was kept — click the mic to retry.";
      }
    },
    async applyVoiceTranscript(text) {
      const transcript = String(text || "").trim();
      if (!transcript) {
        this.notice =
          "Couldn't hear anything in that recording — try again a bit closer to the mic.";
        return;
      }
      // Append to any typed text, then auto-send through the normal flow
      // (send() handles both personal and communal mode). Writing to `input`
      // first means the transcript survives in the textarea if send is
      // blocked (communal lock, missing session, ...).
      const existing = String(this.input || "").trim();
      this.input = existing ? existing + "\n" + transcript : transcript;
      await this.send();
      if (String(this.input || "").trim()) {
        this.notice =
          "Your transcript is ready in the message box — press Send when ready.";
      }
    },
    teardownVoice() {
      if (this.voiceState === "recording" && this.voiceRecorder) {
        this.voiceCancelled = true;
        try {
          this.voiceRecorder.stop();
        } catch (e) {
          // hardware released below either way
        }
      }
      this.releaseVoiceHardware();
      this.voiceState = "idle";
    },
  },
};
